<div class="container-fluid p-3" [class.lv-editor-fill]="selectedConfig">

  @if (!selectedConfig) {
    <!-- ============================ EINGANGS-PORTAL ============================
         Listenliste mit Anzahl + "zuletzt geaendert"-Infos. Erst nach Klick auf
         eine Liste wird der eigentliche Editor angezeigt. -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <button class="btn btn-outline-secondary btn-sm" (click)="goBack()">
        <i class="bi bi-arrow-left"></i> Zurück
      </button>
      <h4 class="mb-0">Listenverwaltung</h4>
    </div>

    <p class="text-muted">
      Wähle eine Liste, um ihre Einträge anzuzeigen und zu bearbeiten. Pro Liste
      siehst du die Anzahl der Einträge und wann sie zuletzt von wem geändert wurde.
    </p>

    <div class="alert alert-warning d-flex align-items-start gap-2">
      <i class="bi bi-exclamation-triangle mt-1"></i>
      <div>
        <strong>Beim Bearbeiten beachten:</strong> Neue Einträge <strong>hinzufügen</strong> ist unproblematisch.
        Das <strong>Ändern oder Löschen</strong> bereits vorhandener Einträge wirkt sich jedoch auf die
        <strong>Validierung bereits erfasster Punkte</strong> aus: Feldwerte, die danach nicht mehr in der Liste
        stehen, werden an den betroffenen Punkten als „nicht in der aktuellen Liste" markiert. Vorhandene Einträge
        daher nur mit Bedacht umbenennen oder entfernen.
      </div>
    </div>

    @if (!canEdit) {
      <div class="alert alert-warning">
        <i class="bi bi-exclamation-triangle"></i> {{ disabledReason }}
      </div>
    }

    <div class="input-group mb-3 lv-portal-search">
      <span class="input-group-text"><i class="bi bi-search"></i></span>
      <input type="text" class="form-control" [(ngModel)]="portalSearchTerm"
        (ngModelChange)="applyPortalFilter()" placeholder="Liste suchen…">
      @if (portalSearchTerm) {
        <button type="button" class="btn btn-outline-secondary" title="Filter löschen"
          (click)="portalSearchTerm = ''; applyPortalFilter()">
          <i class="bi bi-x-lg"></i>
        </button>
      }
    </div>

    <!-- Einmal-Migration: Listen aus der alten Punkte-DB in bpweb-listen übernehmen.
         Idempotent (Marker), Punkte-DB nur lesend. Nur bei DB-Verbindung sinnvoll. -->
    @if (canEdit && !dbComplete) {
      <div class="d-flex justify-content-end mb-2">
        <button type="button" class="btn btn-outline-primary btn-sm"
                (click)="runListMigration()" [disabled]="migrationBusy"
                title="Befüllt die zentrale Listen-DB (bpweb-listen): übernimmt vorhandene Listen aus der Punkte-DB und legt alle fehlenden Standardlisten an. Idempotent.">
          @if (migrationBusy) {
            <span class="spinner-border spinner-border-sm me-1"></span>
          } @else {
            <i class="bi bi-database-down me-1"></i>
          }
          Listen-DB befüllen
        </button>
      </div>
    }

    @if (overviewLoading) {
      <div class="text-muted d-flex align-items-center gap-2">
        <span class="spinner-border spinner-border-sm"></span> Lade Übersicht…
      </div>
    } @else {
      <div class="table-responsive lv-portal">
        <table class="table table-sm table-hover align-middle mb-0">
          <thead class="sticky-top bg-light">
            <tr>
              <th role="button" class="lv-sortable" (click)="sortPortalBy('displayName')">
                Liste <i class="bi" [ngClass]="portalSortCol === 'displayName' ? (portalSortDir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up text-muted'"></i>
              </th>
              <th role="button" class="lv-sortable text-end lv-col-count" (click)="sortPortalBy('count')">
                Einträge <i class="bi" [ngClass]="portalSortCol === 'count' ? (portalSortDir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up text-muted'"></i>
              </th>
              <th role="button" class="lv-sortable" (click)="sortPortalBy('updatedAt')">
                Zuletzt geändert <i class="bi" [ngClass]="portalSortCol === 'updatedAt' ? (portalSortDir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up text-muted'"></i>
              </th>
              <th role="button" class="lv-sortable" (click)="sortPortalBy('updatedBy')">
                Zuletzt geändert von <i class="bi" [ngClass]="portalSortCol === 'updatedBy' ? (portalSortDir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up text-muted'"></i>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (l of filteredOverview; track l.name) {
              <tr class="lv-portal-row" role="button" tabindex="0"
                  (click)="openListFromOverview(l.name)"
                  (keydown.enter)="openListFromOverview(l.name)"
                  title="Liste öffnen">
                <td><i class="bi bi-list-ul me-2 text-secondary"></i>{{ l.displayName }}</td>
                <td class="text-end lv-col-count">{{ l.count }}</td>
                <td>
                  @if (l.updatedAt) {
                    {{ formatDate(l.updatedAt) }}
                  } @else {
                    <span class="text-muted fst-italic">Standarddaten</span>
                  }
                </td>
                <td>{{ l.updatedBy || '–' }}</td>
              </tr>
            } @empty {
              <tr>
                <td colspan="4" class="text-muted text-center py-3">
                  Keine Liste gefunden für „{{ portalSearchTerm }}".
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

  } @else {
    <!-- ============================ LISTEN-EDITOR ============================ -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <button class="btn btn-outline-secondary btn-sm" (click)="backToOverview()">
        <i class="bi bi-arrow-left"></i> Übersicht
      </button>
      <h4 class="mb-0">{{ selectedConfig.displayName }}</h4>
    </div>

    <div class="card">
      <!-- Hinweis wenn nicht berechtigt -->
      @if (!canEdit) {
        <div class="alert alert-warning mb-0 rounded-0 rounded-top">
          <i class="bi bi-exclamation-triangle"></i> {{ disabledReason }}
        </div>
      }

      <div class="card-header">
        <div class="row align-items-center">
          @if (selectedConfig.type !== 'genericTable') {
            <div class="col-md-6">
              <label class="form-label mb-0">Suche:</label>
              <input type="text" class="form-control" [(ngModel)]="searchTerm" (ngModelChange)="applyFilter()"
                placeholder="Filter...">
            </div>
          }
          <div class="col-md-6 text-end">
            <div>
              @if (storageType === 'database') {
                <span class="badge bg-success" title="Verbunden mit zentraler Datenbank">
                  <i class="bi bi-database-check"></i> Datenbank
                </span>
              } @else {
                <span class="badge bg-info" title="Keine DB-Verbindung - Daten werden lokal gespeichert">
                  <i class="bi bi-hdd"></i> Lokal
                </span>
              }
            </div>
            @if (listMetadata) {
              <div class="text-muted" style="font-size: 0.7rem;">
                Zuletzt geändert: {{ listMetadata.updatedBy }}, {{ formatDate(listMetadata.updatedAt) }}
              </div>
            }
          </div>
        </div>

        <!-- Bodensteckbrief: Dropdown-Direktfilter (Substratgruppe/Klassifikation)
             wie im Katalog-Reader, UND-verknüpft mit der Suche. -->
        @if (selectedConfig.type === 'bodensteckbrief') {
          <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
            <select class="form-select form-select-sm lv-bsb-filter"
                    [(ngModel)]="bsbFilterSubstratgruppe" (ngModelChange)="applyFilter()"
                    title="Nach Substratgruppe filtern" aria-label="Nach Substratgruppe filtern">
              <option value="">Substrat: alle</option>
              @for (s of bsbSubstratgruppeOptions; track s) {
                <option [value]="s">{{ s }}</option>
              }
            </select>
            <select class="form-select form-select-sm lv-bsb-filter"
                    [(ngModel)]="bsbFilterKlassifikation" (ngModelChange)="applyFilter()"
                    title="Nach Klassifikation filtern" aria-label="Nach Klassifikation filtern">
              <option value="">Klassifikation: alle</option>
              @for (k of bsbKlassifikationOptions; track k) {
                <option [value]="k">{{ k }}</option>
              }
            </select>
          </div>
        }
      </div>

    <div class="card-body">
      <div class="mb-2 d-flex align-items-center flex-wrap gap-2">
        @if (selectedConfig.type !== 'genericTable') {
          <button type="button" class="btn btn-success btn-sm" (click)="startNewEntry()" [disabled]="!canEdit || showNewEntry || showFbfModal || showBodenartModal">
            <i class="bi bi-plus"></i>
            {{ selectedConfig.type === 'bodensteckbrief' ? 'Neuer Steckbrief' : 'Neuer Eintrag' }}
          </button>
        }
        @if (selectedConfig.type === 'bodensteckbrief') {
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="openNamensmatrix()"
                  title="Namensmatrix: Kürzel × Substrat als klickbare Kreuztabelle">
            <i class="bi bi-grid-3x3-gap"></i> Namensmatrix
          </button>
        }
        @if (canImportList) {
          <label class="btn btn-outline-secondary btn-sm mb-0" [class.disabled]="!canEdit"
                 title="Ersetzt die Liste durch ein hochgeladenes JSON-Array (mit Rückfrage). Anschließend prüfen und „Speichern“.">
            <i class="bi bi-upload"></i> Import (JSON)
            <input type="file" accept="application/json,.json" hidden [disabled]="!canEdit" (change)="onImportListFile($event)">
          </label>
        }
        @if (selectedConfig.type !== 'genericTable') {
          <span class="ms-3 text-muted">{{ filteredData.length }} Einträge</span>
        }

        <!-- Speichern / Verwerfen rechts in der Toolbar (war frueher Card-Footer). -->
        <div class="ms-auto d-flex align-items-center gap-2">
          @if (hasChanges) {
            <span class="text-warning small">
              <i class="bi bi-exclamation-triangle"></i> Ungespeicherte Änderungen
            </span>
          }
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="openHistory()"
                  title="Frühere Stände dieser Liste ansehen und wiederherstellen">
            <i class="bi bi-clock-history"></i> Verlauf
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="discardChanges()"
                  [disabled]="!hasChanges"
                  title="Verwirft alle ungespeicherten Änderungen">
            <i class="bi bi-x-circle"></i> Verwerfen
          </button>
          <button type="button" class="btn btn-primary btn-sm" (click)="saveList()"
                  [disabled]="!canEdit || !hasChanges"
                  [title]="saveDisabledReason || 'Speichert die Liste in die zentrale Datenbank'">
            <i class="bi bi-download"></i> Speichern
          </button>
        </div>
      </div>

      <!-- Punktarten: dedizierter Editor mit Nummer/Beschreibung/Kategorie + SVG-Picker/Upload -->
      @if (selectedConfig.name === 'Punktarten') {
        <app-punktarten-editor [data]="listData" [readonly]="!canEdit"
                               (changed)="hasChanges = true"></app-punktarten-editor>
      }

      <!-- Generischer Tabellen-Editor (Objekt-/String-Listen ohne dediziertes UI) -->
      @if (selectedConfig.type === 'genericTable' && selectedConfig.name !== 'Punktarten') {
        <app-generic-list-editor [data]="listData" [readonly]="!canEdit"
                                 (changed)="hasChanges = true"></app-generic-list-editor>
      }

      <!-- Neuer Eintrag Formular (für simple/valueNamePair) -->
      @if (showNewEntry && selectedConfig.type !== 'feinbodenform') {
        <div class="alert alert-success p-2 mb-2">
          <div class="row align-items-center">
            @if (selectedConfig.type === 'simple') {
              <div class="col-4">
                <input type="text" class="form-control form-control-sm" [(ngModel)]="newValue"
                  placeholder="Neuer Wert" (keyup.enter)="addNewEntry()" (keyup.escape)="cancelNewEntry()">
              </div>
              <div class="col">
                <input type="text" class="form-control form-control-sm" [(ngModel)]="newErklaerung"
                  placeholder="Erklärung (optional)" (keyup.enter)="addNewEntry()" (keyup.escape)="cancelNewEntry()">
              </div>
            } @else {
              <div class="col-5">
                <input type="text" class="form-control form-control-sm" [(ngModel)]="newValue"
                  placeholder="Wert" (keyup.enter)="addNewEntry()" (keyup.escape)="cancelNewEntry()">
              </div>
              <div class="col-5">
                <input type="text" class="form-control form-control-sm" [(ngModel)]="newName"
                  placeholder="Name" (keyup.enter)="addNewEntry()" (keyup.escape)="cancelNewEntry()">
              </div>
            }
            <div class="col-auto">
              <button class="btn btn-success btn-sm" (click)="addNewEntry()">
                <i class="bi bi-check"></i>
              </button>
              <button class="btn btn-secondary btn-sm" (click)="cancelNewEntry()">
                <i class="bi bi-x"></i>
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Tabelle (typ-spezifisch; genericTable rendert stattdessen den generischen Editor oben) -->
      @if (selectedConfig.type !== 'genericTable') {
      <div class="table-responsive lv-edit-scroll">
        <table class="table table-sm table-striped table-hover">
          <thead class="sticky-top bg-light">
            <tr>
              @if (selectedConfig.type === 'simple') {
                <th style="width: 30%">Wert</th>
                <th>Erklärung <span class="text-muted fw-normal small">(optional, z.B. für Hilfen)</span></th>
              } @else if (selectedConfig.type === 'bodenartenTabelle') {
                <th>Kürzel</th>
                <th>LD ger.</th>
                <th>LD mit.</th>
                <th>LD hoch</th>
                <th>h2</th>
                <th>h3</th>
                <th>h4</th>
                <th>h5</th>
                <th>h6</th>
              } @else if (selectedConfig.type === 'feinbodenform') {
                <th>FBF</th>
                <th>Anzeige</th>
                <th>Lokalname</th>
                <th>HBF (lang)</th>
                <th>HBF</th>
                <th>Substratgr.</th>
                <th>Nährkraft</th>
                <th>Seite</th>
              } @else if (selectedConfig.type === 'bodensteckbrief') {
                <th class="lv-sortable col-bsb-substr" role="button" (click)="setBsbSort('substratgruppe')">
                  Substratgr. <i class="bi {{ bsbSortIcon('substratgruppe') }}"></i>
                </th>
                <th class="lv-sortable col-bsb-klass" role="button" (click)="setBsbSort('klassifikation')">
                  Klassifikation <i class="bi {{ bsbSortIcon('klassifikation') }}"></i>
                </th>
                <th class="lv-sortable col-bsb-abk" role="button" (click)="setBsbSort('abkuerzung')">
                  HBF Abk. <i class="bi {{ bsbSortIcon('abkuerzung') }}"></i>
                </th>
                <th class="lv-sortable" role="button" (click)="setBsbSort('name')">
                  HBF Name <i class="bi {{ bsbSortIcon('name') }}"></i>
                </th>
                <th class="lv-sortable col-bsb-nfbf text-end pe-4" role="button" (click)="setBsbSort('fbf')"
                    title="Anzahl Feinbodenformen">
                  #FBF <i class="bi {{ bsbSortIcon('fbf') }}"></i>
                </th>
                <th class="lv-sortable col-bsb-seite text-end pe-3" role="button" (click)="setBsbSort('seitenzahl')">
                  Seitenzahl <i class="bi {{ bsbSortIcon('seitenzahl') }}"></i>
                </th>
              } @else {
                <th style="width: 30%">Wert</th>
                <th style="width: 50%">Name</th>
              }
              <th class="text-center" style="width: 130px">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            @for (item of filteredData; track $index) {
              <tr [attr.data-entry-id]="item?.id"
                  [class.lv-row-highlight]="item?.id && item.id === highlightedEntryId">
                @if (selectedConfig.type === 'simple') {
                  <!-- Einfache Liste: Wert + optionale Erklärung -->
                  @if (isEditing($index)) {
                    <td>
                      <input type="text" class="form-control form-control-sm" [(ngModel)]="editValue"
                        (keyup.enter)="saveEdit()" (keyup.escape)="cancelEdit()">
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" [(ngModel)]="editErklaerung"
                        placeholder="Erklärung (optional)" (keyup.enter)="saveEdit()" (keyup.escape)="cancelEdit()">
                    </td>
                  } @else {
                    <td>{{ simpleValue(item) }}</td>
                    <td class="text-muted small">{{ simpleExplanation(item) }}</td>
                  }
                } @else if (selectedConfig.type === 'bodenartenTabelle') {
                  <!-- BodenartenTabelle -->
                  <td>{{ item.name }}</td>
                  <td>{{ item.Lagerungsdichte?.gering }}</td>
                  <td>{{ item.Lagerungsdichte?.mittel }}</td>
                  <td>{{ item.Lagerungsdichte?.hoch }}</td>
                  <td>{{ item.Humusgehalt?.h2 }}</td>
                  <td>{{ item.Humusgehalt?.h3 }}</td>
                  <td>{{ item.Humusgehalt?.h4 }}</td>
                  <td>{{ item.Humusgehalt?.h5 }}</td>
                  <td>{{ item.Humusgehalt?.h6 }}</td>
                } @else if (selectedConfig.type === 'feinbodenform') {
                  <!-- Feinbodenform -->
                  <td>{{ item.FBF }}</td>
                  <td>{{ item.FBF_display }}</td>
                  <td>{{ item.name }}</td>
                  <td>{{ item.HBF_lang || '-' }}</td>
                  <td>{{ item.HBF }}</td>
                  <td>{{ item.STG || '-' }}</td>
                  <td>{{ item.NKr }}</td>
                  <td>{{ item.Seite || '-' }}</td>
                } @else if (selectedConfig.type === 'bodensteckbrief') {
                  <!-- Bodensteckbrief: Spalten + Reihenfolge wie im Katalog-Reader -->
                  <td>{{ item.hauptbodenform?.substratgruppe || '–' }}</td>
                  <td>{{ item.klassifikation || '–' }}</td>
                  <td><span class="badge bg-light text-dark border">{{ item.hauptbodenform?.abkuerzung || '–' }}</span></td>
                  <td>{{ item.hauptbodenform?.name || '(ohne Name)' }}</td>
                  <td class="text-end pe-4">{{ item.feinbodenformen?.length || 0 }}</td>
                  <td class="text-end pe-3">{{ item.seitenzahl || '–' }}</td>
                } @else {
                  <!-- Value/Name Paar -->
                  @if (isEditing($index)) {
                    <td>
                      <input type="text" class="form-control form-control-sm" [(ngModel)]="editValue"
                        (keyup.enter)="saveEdit()" (keyup.escape)="cancelEdit()">
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" [(ngModel)]="editName"
                        (keyup.enter)="saveEdit()" (keyup.escape)="cancelEdit()">
                    </td>
                  } @else {
                    <td>{{ item.value }}</td>
                    <td>{{ item.name }}</td>
                  }
                }
                <td class="lv-actions-cell">
                  <div class="d-flex flex-wrap justify-content-center align-items-center gap-1">
                  @if (isEditing($index) && selectedConfig.type !== 'feinbodenform') {
                    <button class="btn btn-success btn-sm me-1" (click)="saveEdit()" title="Speichern">
                      <i class="bi bi-check"></i>
                    </button>
                    <button class="btn btn-secondary btn-sm" (click)="cancelEdit()" title="Abbrechen">
                      <i class="bi bi-x"></i>
                    </button>
                  } @else {
                    <button class="btn btn-outline-primary btn-sm me-1" (click)="startEdit($index)" title="Bearbeiten" [disabled]="!canEdit">
                      <i class="bi bi-pencil"></i>
                    </button>
                    @if (selectedConfig.type === 'bodensteckbrief') {
                      <button class="btn btn-outline-secondary btn-sm me-1" (click)="previewSteckbrief($index)" title="Vorschau">
                        <i class="bi bi-eye"></i>
                      </button>
                    }
                    <button class="btn btn-outline-danger btn-sm" (click)="deleteEntry($index)" title="Löschen" [disabled]="!canEdit">
                      <i class="bi bi-trash"></i>
                    </button>
                  }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      }
    </div>
    </div>
  }
</div>

<!-- Feinbodenform Modal -->
@if (showFbfModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ fbfEditMode === 'new' ? 'Neue Feinbodenform' : 'Feinbodenform bearbeiten' }}
          </h5>
          <button type="button" class="btn-close" (click)="closeFbfModal()"></button>
        </div>
        <div class="modal-body">
          @if (fbfEditIsDerived) {
            <div class="alert alert-info d-flex align-items-start gap-2 py-2">
              <i class="bi bi-info-circle mt-1"></i>
              <div class="small">
                Die <strong>grau hinterlegten Felder</strong> kommen aus dem
                <strong>Hauptbodenformenkatalog</strong> und werden dort gepflegt.
                Hier bearbeitest du nur <strong>Hinweis</strong> und <strong>Zuordnungen</strong>.
              </div>
            </div>
          }
          <div class="row mb-3">
            <div class="col-md-4">
              <label class="form-label">FBF</label>
              <input type="text" class="form-control" [(ngModel)]="fbfEdit.FBF" placeholder="z.B. AbdLU"
                     [readonly]="fbfEditIsDerived" [class.bg-light]="fbfEditIsDerived">
            </div>
            <div class="col-md-4">
              <label class="form-label">Anzeige (FBF_display)</label>
              <input type="text" class="form-control" [(ngModel)]="fbfEdit.FBF_display" placeholder="z.B. Ab dLU"
                     [readonly]="fbfEditIsDerived" [class.bg-light]="fbfEditIsDerived">
            </div>
            <div class="col-md-4">
              <label class="form-label">Lokalname</label>
              <input type="text" class="form-control" [(ngModel)]="fbfEdit.name" placeholder="z.B. Annaburger"
                     [readonly]="fbfEditIsDerived" [class.bg-light]="fbfEditIsDerived">
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label">HBF</label>
              <div class="input-group">
                <input type="text" class="form-control" [(ngModel)]="fbfEdit.HBF" placeholder="z.B. l/s UA"
                       [readonly]="fbfEditIsDerived" [class.bg-light]="fbfEditIsDerived">
                <button type="button" class="btn btn-outline-secondary"
                        (click)="openBodenformenkatalog(fbfEdit.HBF)"
                        [disabled]="!fbfEdit.HBF"
                        title="Im Bodenformen-Katalog nachschlagen">
                  <i class="bi bi-info-circle"></i>
                </button>
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label">HBF (lang)</label>
              <input type="text" class="form-control" [(ngModel)]="fbfEdit.HBF_lang" placeholder="z.B. Decklehm-Grauamphigley"
                     [readonly]="fbfEditIsDerived" [class.bg-light]="fbfEditIsDerived">
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-md-3">
              <label class="form-label">Nährkraftstufe</label>
              <input type="text" class="form-control" [(ngModel)]="fbfEdit.NKr" placeholder="z.B. K"
                     [readonly]="fbfEditIsDerived" [class.bg-light]="fbfEditIsDerived">
            </div>
            <div class="col-md-3">
              <label class="form-label">Seite</label>
              <input type="text" class="form-control" [(ngModel)]="fbfEdit.Seite" placeholder="z.B. 398/1"
                     [readonly]="fbfEditIsDerived" [class.bg-light]="fbfEditIsDerived">
            </div>
            <div class="col-md-3">
              <label class="form-label">Hinweis</label>
              <input type="text" class="form-control" [(ngModel)]="fbfEdit.Hw" placeholder="optional">
            </div>
            <div class="col-md-3">
              <label class="form-label">Serie</label>
              <input type="text" class="form-control" [(ngModel)]="fbfEdit.Serie" placeholder="z.B. VIII + o -"
                     [readonly]="fbfEditIsDerived" [class.bg-light]="fbfEditIsDerived">
            </div>
          </div>

          <!-- Zuordnungen -->
          <div class="card">
            <div class="card-header py-2">
              <strong>Zuordnungen</strong>
            </div>
            <div class="card-body p-2">
              <div class="table-responsive" style="max-height: 200px; overflow-y: auto;">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th style="width: 40%">Wasserform</th>
                      <th style="width: 40%">STFG</th>
                      <th style="width: 20%"></th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (key of zuordnungKeys; track key) {
                      <tr>
                        <td>{{ key }}</td>
                        <td>
                          <input type="text" class="form-control form-control-sm"
                            [ngModel]="fbfEdit.zuordnung[key]"
                            (ngModelChange)="updateZuordnung(key, $event)">
                        </td>
                        <td>
                          <button class="btn btn-outline-danger btn-sm" (click)="removeZuordnung(key)">
                            <i class="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    }
                    <!-- Neue Zuordnung -->
                    <tr>
                      <td>
                        <input type="text" class="form-control form-control-sm" [(ngModel)]="newZuordnungKey"
                          placeholder="Neue Wasserform" (keyup.enter)="addZuordnung()">
                      </td>
                      <td>
                        <input type="text" class="form-control form-control-sm" [(ngModel)]="newZuordnungValue"
                          placeholder="STFG" (keyup.enter)="addZuordnung()">
                      </td>
                      <td>
                        <button class="btn btn-success btn-sm" (click)="addZuordnung()">
                          <i class="bi bi-plus"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeFbfModal()">Abbrechen</button>
          <button type="button" class="btn btn-primary" (click)="saveFbfEntry()">Übernehmen</button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Bodenart Modal -->
@if (showBodenartModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ bodenartEditMode === 'new' ? 'Neue Bodenart' : 'Bodenart bearbeiten' }}
          </h5>
          <button type="button" class="btn-close" (click)="closeBodenartModal()"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Kürzel</label>
            <input type="text" class="form-control" [(ngModel)]="bodenartEdit.name" placeholder="z.B. mk">
          </div>
          <div class="card mb-3">
            <div class="card-header py-2"><strong>Lagerungsdichte (nFK je dm)</strong></div>
            <div class="card-body p-2">
              <div class="row g-2">
                <div class="col-4">
                  <label class="form-label">gering</label>
                  <input type="number" class="form-control form-control-sm" [(ngModel)]="bodenartEdit.Lagerungsdichte.gering" step="0.1">
                </div>
                <div class="col-4">
                  <label class="form-label">mittel</label>
                  <input type="number" class="form-control form-control-sm" [(ngModel)]="bodenartEdit.Lagerungsdichte.mittel" step="0.1">
                </div>
                <div class="col-4">
                  <label class="form-label">hoch</label>
                  <input type="number" class="form-control form-control-sm" [(ngModel)]="bodenartEdit.Lagerungsdichte.hoch" step="0.1">
                </div>
              </div>
            </div>
          </div>
          <div class="card">
            <div class="card-header py-2"><strong>Humusgehalt-Zuschlag</strong></div>
            <div class="card-body p-2">
              <div class="row g-2">
                <div class="col">
                  <label class="form-label">h2</label>
                  <input type="number" class="form-control form-control-sm" [(ngModel)]="bodenartEdit.Humusgehalt.h2" step="0.1">
                </div>
                <div class="col">
                  <label class="form-label">h3</label>
                  <input type="number" class="form-control form-control-sm" [(ngModel)]="bodenartEdit.Humusgehalt.h3" step="0.1">
                </div>
                <div class="col">
                  <label class="form-label">h4</label>
                  <input type="number" class="form-control form-control-sm" [(ngModel)]="bodenartEdit.Humusgehalt.h4" step="0.1">
                </div>
                <div class="col">
                  <label class="form-label">h5</label>
                  <input type="number" class="form-control form-control-sm" [(ngModel)]="bodenartEdit.Humusgehalt.h5" step="0.1">
                </div>
                <div class="col">
                  <label class="form-label">h6</label>
                  <input type="number" class="form-control form-control-sm" [(ngModel)]="bodenartEdit.Humusgehalt.h6" step="0.1">
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeBodenartModal()">Abbrechen</button>
          <button type="button" class="btn btn-primary" (click)="saveBodenartEntry()">Übernehmen</button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Versions-Verlauf Modal -->
@if (showHistoryModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-clock-history me-1"></i> Verlauf: {{ selectedConfig?.displayName }}
          </h5>
          <button type="button" class="btn-close" (click)="closeHistory()"></button>
        </div>
        <div class="modal-body">
          @if (historyLoading) {
            <div class="text-muted d-flex align-items-center gap-2">
              <span class="spinner-border spinner-border-sm"></span> Lade Verlauf…
            </div>
          } @else if (historyVersions.length === 0) {
            <p class="text-muted mb-0">
              Für diese Liste sind noch keine früheren Stände gespeichert. Sobald du Änderungen
              speicherst, wird der jeweils vorherige Stand hier hinterlegt.
            </p>
          } @else {
            <p class="text-muted small">
              Frühere Stände dieser Liste, neueste zuerst. „Wiederherstellen" macht den gewählten
              Stand wieder zum aktuellen — der jetzige Stand bleibt dabei im Verlauf erhalten.
            </p>
            <div class="table-responsive">
              <table class="table table-sm table-hover align-middle mb-0">
                <thead class="sticky-top bg-light">
                  <tr>
                    <th>Stand vom</th>
                    <th>Geändert von</th>
                    <th class="text-end">Einträge</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (v of historyVersions; track v.id) {
                    <tr>
                      <td>
                        {{ formatDate(v.updatedAt) || formatDate(v.savedAt) || '—' }}
                        <span class="text-muted small d-block">{{ versionActionLabel(v.action) }}</span>
                      </td>
                      <td>{{ v.updatedBy || '–' }}</td>
                      <td class="text-end">{{ v.count }}</td>
                      <td class="text-end">
                        <button type="button" class="btn btn-outline-primary btn-sm"
                                (click)="restoreVersion(v)" [disabled]="!canEdit"
                                title="Diesen Stand wiederherstellen">
                          <i class="bi bi-arrow-counterclockwise"></i> Wiederherstellen
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeHistory()">Schließen</button>
        </div>
      </div>
    </div>
  </div>
}

